import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import useSetPdfBoundingBoxes from '~/common/useActionMapper/useSetPdfBoundingBoxes';

import { Checkbox } from '../Checkbox';
import Input from '../Input/Input';
import type { Citation } from '../PdfViewer/types';
import Select from '../Select/Select';

type FocusableElement = {
  focus: () => void;
};

type ComponentType = 'Input' | 'Checkbox' | 'Select';

type LabelInputSwitchProps = {
  name: string;
  componentType?: ComponentType;
  onFocus?: React.FocusEventHandler<HTMLElement>;
};

const LabelInputSwitch = forwardRef<FocusableElement, LabelInputSwitchProps>(
  ({ componentType, onFocus, ...rest }, ref) => {
    switch (componentType) {
      case 'Checkbox':
        return <Checkbox {...(rest as any)} ref={ref} />;

      case 'Select':
        return <Select {...rest} options={[]} ref={ref} onFocus={onFocus} />;

      case 'Input':
      default:
        return <Input {...rest} ref={ref} onFocus={onFocus} />;
    }
  },
);

LabelInputSwitch.displayName = 'LabelInputSwitch';

type LabelInputProps = {
  name: string;
  snippets?: Citation[];
  hasExtraction?: boolean;
  toggleInputOnSelection?: boolean;
  focusInputOnSelection?: boolean;
  shouldToggleFocusForDatePicker?: boolean;
  selectedOption?: string | number;
  componentType?: ComponentType;
  optionLabel?: string;
  onChange?: (optionLabel: string) => void;
};

const LabelInput = forwardRef<FocusableElement, LabelInputProps>(
  (
    {
      snippets = [],
      toggleInputOnSelection = false,
      focusInputOnSelection = false,
      optionLabel = '',
      selectedOption = '',
      onChange = () => {},
      componentType = 'Input',
      ...rest
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<FocusableElement | null>(null);
    const setPdfBoundingBoxes = useSetPdfBoundingBoxes();

    useImperativeHandle(forwardedRef, () => inputRef.current, []);

    const showInput = !toggleInputOnSelection || selectedOption === optionLabel;

    const handleOnFocus = useCallback(() => {
      onChange(optionLabel);
      setPdfBoundingBoxes(snippets, rest.name)();
    }, [onChange, optionLabel, setPdfBoundingBoxes, snippets, rest.name]);

    useEffect(() => {
      if (showInput && focusInputOnSelection) {
        inputRef.current?.focus();
      }
    }, [showInput, focusInputOnSelection]);

    if (!showInput) {
      return null;
    }

    return (
      <LabelInputSwitch
        {...rest}
        ref={inputRef}
        componentType={componentType}
        onFocus={handleOnFocus}
      />
    );
  },
);

LabelInput.displayName = 'LabelInput';

export default LabelInput;
